import { useQuery } from "@tanstack/react-query";
import { getCurrencies, ALL_FIAT_CODES, type CurrencyOption } from "@/api/client";

// ALL_FIAT_CODES as {code, label: code} — used both as placeholderData (so
// pickers render immediately instead of an empty state while the first fetch
// is in flight) and as the hard fallback below. placeholderData only applies
// while the query is "pending"; once a fetch attempt fails and settles into
// "error" (cold start, deploy blip, network failure), TanStack Query drops
// back to `data === undefined` — so without the `?? FALLBACK_CURRENCIES`
// below, every currency picker in the app (including the pre-login signup
// form) would render with zero options on any transient backend hiccup.
const FALLBACK_CURRENCIES: CurrencyOption[] = ALL_FIAT_CODES.map((code) => ({
  code,
  label: code,
}));

// Single source of truth for the app's supported-currency list, fetched from
// GET /currencies (backed by core/constants.USER_SELECTABLE_CURRENCIES on the
// backend). Currencies change rarely, so this is cached for the whole session
// — components should use this instead of hardcoding their own currency list.
export const useCurrencies = () => {
  const { data, ...rest } = useQuery<CurrencyOption[]>({
    queryKey: ["currencies"],
    queryFn: getCurrencies,
    staleTime: Infinity,
    gcTime: Infinity,
    placeholderData: FALLBACK_CURRENCIES,
  });

  return { currencies: data ?? FALLBACK_CURRENCIES, ...rest };
};
